import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useMutation } from '@tanstack/react-query';
import {
  jobsApi,
  type PrintDeliveryDocketRequest,
  type PrintDeliveryDocketResponse,
} from '@/lib/jobs';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (resp: PrintDeliveryDocketResponse) => void;
  jobId: number;
  jobStatus: string;
  rowVersionBase64: string;
}

export function DispatchModal({
  open,
  onClose,
  onSaved,
  jobId,
  jobStatus,
  rowVersionBase64,
}: Props) {
  const isCompleted = jobStatus === 'Completed';
  const isDispatched = jobStatus === 'Dispatched';
  const checkboxVisible = isCompleted || isDispatched;
  const checkboxDisabled = isDispatched;

  const [parcelCount, setParcelCount] = useState<number>(1);
  const [itemSummary, setItemSummary] = useState<string>('');
  const [courier, setCourier] = useState<string>('');
  const [tracking, setTracking] = useState<string>('');
  const [dispatchedDate, setDispatchedDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [markDispatched, setMarkDispatched] = useState<boolean>(isCompleted);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (req: PrintDeliveryDocketRequest) => jobsApi.printDeliveryDocket(jobId, req),
    onSuccess: (resp) => {
      window.open(resp.pdfUrl, '_blank');
      onSaved(resp);
      onClose();
    },
    onError: (e: unknown) => {
      const status = (e as { status?: number })?.status;
      if (status === 409) setError('Job was changed by another user. Refresh and retry.');
      else if (status === 422) setError('Cannot mark dispatched from current status.');
      else setError('Print failed. Please try again.');
    },
  });

  const submit = () => {
    if (parcelCount < 1) {
      setError('Parcels must be at least 1.');
      return;
    }
    if (!itemSummary.trim()) {
      setError('Item summary is required.');
      return;
    }
    setError(null);
    mutation.mutate({
      parcelCount,
      itemSummary,
      courier: courier || null,
      trackingNumber: tracking || null,
      dispatchedDate: markDispatched && !checkboxDisabled ? dispatchedDate : null,
      markDispatched: markDispatched && !checkboxDisabled,
      rowVersionBase64,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delivery docket</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="parcelCount">Number of parcels</Label>
            <Input
              id="parcelCount"
              type="number"
              min={1}
              max={999}
              value={parcelCount}
              onChange={(e) => setParcelCount(Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label htmlFor="itemSummary">Item summary</Label>
            <textarea
              id="itemSummary"
              maxLength={1000}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={itemSummary}
              onChange={(e) => setItemSummary(e.target.value)}
              aria-label="Item summary"
            />
          </div>
          <div>
            <Label htmlFor="courier">Courier</Label>
            <Input
              id="courier"
              maxLength={100}
              value={courier}
              onChange={(e) => setCourier(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="tracking">Tracking number</Label>
            <Input
              id="tracking"
              maxLength={100}
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
            />
          </div>
          {checkboxVisible && (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  id="markDispatched"
                  checked={markDispatched}
                  disabled={checkboxDisabled}
                  onCheckedChange={(c) => setMarkDispatched(c === true)}
                  aria-label="Mark this Job as Dispatched after print"
                />
                <span>Mark this Job as Dispatched after print</span>
              </label>
              {markDispatched && !checkboxDisabled && (
                <div>
                  <Label htmlFor="dispatchedDate">Dispatched date</Label>
                  <Input
                    id="dispatchedDate"
                    type="date"
                    value={dispatchedDate}
                    onChange={(e) => setDispatchedDate(e.target.value)}
                  />
                </div>
              )}
            </>
          )}
          {error && (
            <p className="text-red-600 text-sm" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Printing…' : 'Print'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
